import {
  deleteInstanceEndpoint,
  getInstanceThumbnailEndpoint,
  processInstanceEndpoint,
  rerunLandmarkingEndpoint,
  unvalidateInstanceEndpoint,
  updateInstanceEndpoint,
  uploadInstanceEndpoint,
  validateInstanceEndpoint,
} from "./api/Instances";

export const validateInstanceInDatabase = async instanceId => {
  try {
    const response = await validateInstanceEndpoint(instanceId);
    return response;
  } catch (error) {
    throw new Error(`Error validating instance: ${error.message}`);
  }
};

export const unvalidateInstanceInDatabase = async instanceId => {
  try {
    const response = await unvalidateInstanceEndpoint(instanceId);
    return response;
  } catch (error) {
    throw new Error(`Error unvalidating instance: ${error.message}`);
  }
};

export const getInstanceThumbnail = (patientId, instanceId) => {
  return getInstanceThumbnailEndpoint(patientId, instanceId)
    .then(response => {
      return response;
    })
    .catch(error => {
      throw new Error(`Error fetching the image: ${error.message}`);
    });
};

export const postPatientMedicalImage = async (patientId, file, destination) => {
  return uploadInstanceEndpoint(patientId, file, destination)
    .then(response => {
      return response;
    })
    .catch(error => {
      throw new Error(`Error fetching the image: ${error.message}`);
    });
};

// Processing queue to handle sequential processing
let processingQueue = Promise.resolve();

export const postPatientMedicalImages = async (
  patientId,
  files,
  metadata = {},
  callbacks = {}
) => {
  const { onUploadComplete, onProcessingComplete } = callbacks;

  const destination = Array.from(
    { length: 16 },
    () =>
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"[
        Math.floor(Math.random() * 62)
      ]
  ).join("");

  // Upload all files first
  const uploadResults = await Promise.all(
    files.map(file => {
      return postPatientMedicalImage(patientId, file, destination);
    })
  );

  // Notify upload complete
  onUploadComplete?.(uploadResults);

  // Add processing to queue (runs in background, doesn't block)
  processingQueue = processingQueue
    .then(() => {
      // Add a small delay before processing
      return new Promise(res => setTimeout(res, 1000));
    })
    .then(() => {
      // Process this batch (backend: metadata extraction, NIfTI conversion, thumbnails, study creation)
      return processInstanceEndpoint(patientId, metadata, destination);
    })
    .then(response => {
      if (response.success !== false) {
        onProcessingComplete?.(true, response);
      } else {
        onProcessingComplete?.(false, new Error("Processing failed"));
      }
    })
    .catch(error => {
      console.error("[InstancesService] Backend processing error:", error);
      onProcessingComplete?.(false, error);
    });

  // Return upload results immediately (don't block on processing)
  return uploadResults;
};

export const rerunInstanceLandmarking = async (instanceId, metadata = {}) => {
  try {
    const response = await rerunLandmarkingEndpoint(instanceId, metadata);
    return response;
  } catch (error) {
    throw new Error(`Error rerunning landmarking: ${error.message}`);
  }
};

export const updateInstance = async (instanceId, payload) => {
  try {
    const response = await updateInstanceEndpoint(instanceId, payload);
    return response;
  } catch (error) {
    throw new Error(`Error updating instance: ${error.message}`);
  }
};

// Backward-compatible wrapper for metadata updates
export const updateInstanceMetadata = async (instanceId, metadata = {}) => {
  return updateInstance(instanceId, metadata);
};

export const deleteInstance = async instanceId => {
  try {
    const response = await deleteInstanceEndpoint(instanceId);
    return response;
  } catch (error) {
    throw new Error(`Error deleting instance: ${error.message}`);
  }
};

// Backward-compatible wrapper for display settings updates
export const updateInstanceDisplaySettings = async (
  instanceId,
  viewType,
  brightness,
  contrast
) => {
  return updateInstance(instanceId, {
    displaySettings: {
      [viewType]: { brightness, contrast },
    },
  });
};
